import { Branch } from './branch';
import { Contact } from './contact';
import { File } from './file.model';
import { User } from './user.model';
import { InspectionTemplateUsage } from './inspection-template.model';
import { InspectionItemBinding } from './inspection-item-binding.enum';
import { InspectionItemType } from './inspection-item-type.enum';
import { NotesInputType } from './notes-input-type.enum';
import { NumericRangeConstraints } from './numeric-range-constraints.model';
import { TriStateValue } from './tri-state-value.enum';
import type { Customer } from './customer.model';
import type { Vehicle } from './vehicle.model';
import type { Job } from './job.model';
import { Quote } from './quote.model';
import { WorkOrder } from './work-order.model';

/**
 * Inspection entity
 * Runtime instance of an inspection created from a template
 */
export interface Inspection {
    id: string;
    branchId: string;
    branch?: Branch;
    tenantId: string;
    customerId: string;
    customer?: Customer;
    appointmentId?: string;
    vehicleId: string;
    vehicle?: Vehicle;
    createdDate?: Date;
    createdBy?: string;
    modifiedDate?: Date;
    modifiedBy?: string;
    advisor?: User;
    advisorId?: string;
    assignedDate?: Date;
    completedDate?: Date;
    customerNotes?: string;
    isAssigned?: boolean;
    isCompleted?: boolean;
    isLocked?: boolean;
    isSent?: boolean;
    isSubmitted?: boolean;
    name: string;
    primaryContact?: Contact;
    sections: InspectionSection[];
    sentDate?: Date;
    technician?: User;
    technicianId?: string;
    templateId: string;
    /**
     * Denormalised from the originating template at creation. Needed to tell a handback (QC) from a
     * check-in or standard inspection, because a handback is exempt from the advisor review gate and so
     * must still offer Transfer while the gate is active.
     */
    usage?: InspectionTemplateUsage;
    defaultAllItemsToPass?: boolean;
    useAISuggestion?: boolean;
    useAIKnowledgeTipsOnWarning?: boolean;
    useAIKnowledgeTipsOnFail?: boolean;
    includeWarningsOnTransfer?: boolean;
    requireNotesOnFail?: boolean;
    /**
     * Denormalised from the originating template. When true the backend rejects a transfer of an
     * unsigned inspection, so the tablet collects the technician signature before transferring.
     */
    requireTechnicianSignature?: boolean;
    /**
     * Blob internal name of the technician signature PNG, resolved for display the same way as other
     * stored files. Null until the technician signs.
     */
    technicianSignatureImageUrl?: string | null;
    /**
     * UTC timestamp at which the technician signed. Null until signed.
     */
    technicianSignedAt?: Date | null;
    /**
     * The user who signed. Null until signed.
     */
    technicianSignedBy?: User | null;
    requireNotesOnWarning?: boolean;
    requirePhotoOrVideoOnFail?: boolean;
    requirePhotoOrVideoOnWarning?: boolean;
    /**
     * When true on the parent template, technicians at inspection time may pick only
     * options from the per-item catalog -- the freehand toolbar in the
     * inspection-note-options picker is hidden and `MaterializeFreehandNoteOptions` is a
     * server-side no-op for this template. Default false preserves freehand + AI behavior.
     */
    restrictTechAddNoteOptions?: boolean;
    status?: string;
    lastVerifiedDate?: Date;
    failCount?: number;
    warningCount?: number;
    passCount?: number;
    totalItems?: number;
    /**
     * Direct FK to the target Quote (exactly one of quoteId / workOrderId is set
     * at creation, enforced by InspectionService.Create and a DB CHECK).
     */
    quoteId?: string;
    quote?: Quote;
    workOrderId?: string;
    workOrder?: WorkOrder;
    /**
     * UTC timestamp captured when a technician skipped this inspection instead of completing it
     * item-by-item. Null/undefined for inspections that were not skipped. Mirrors the backend
     * InspectionOutputMessage.SkippedAt field; its presence is the sole signal that an inspection
     * is in the skipped state (there is no server-side status enum value for "Skipped").
     */
    skippedAt?: Date;
    /**
     * Identifier of the tenant-curated InspectionSkipReason selected at skip time (FK to the reason's
     * id). Mirrors the backend InspectionOutputMessage.SkipReasonId. Null/undefined when the inspection
     * was not skipped. The reason row is retained by soft-delete when retired, so this reference stays
     * resolvable for historical skips.
     */
    skipReasonId?: string;
    /**
     * Identifier of the Raptor user who skipped the inspection. Null/undefined when the
     * inspection was not skipped.
     */
    skippedByUserId?: string;
    /**
     * UTC timestamp captured when a technician submitted this inspection for advisor review, moving it
     * into the pending-review state ahead of transfer. Null/undefined until the inspection is submitted.
     * Mirrors the backend InspectionOutputMessage.SubmittedForReviewDate; combined with a null
     * completedDate it is the signal that the inspection is awaiting advisor review.
     */
    submittedForReviewDate?: Date | null;
}

/**
 * Section within an inspection
 * Groups related inspection items together
 * Supports nested sections (max 2 levels deep)
 */
export interface InspectionSection {
    id: string;
    branchId: string;
    tenantId: string;
    inspectionId: string;
    jobId?: string;
    job?: Job;
    items: InspectionItem[];
    name: string;
    order: number;
    filter?: InspectionSectionFilter<InspectionItem>;
    parentSectionId?: string | null;
    childSections: InspectionSection[];
}

/**
 * Filter configuration for inspection section
 */
export interface InspectionSectionFilter<T extends InspectionItemBase> {
    name: string | null;
    property: keyof T | null;
    value: string | boolean | number | null;
}

/**
 * Sidecar carried alongside an inspection item save.
 *
 * Tech-authored freehand notes carry an optional single recommendation. The server
 * materializes new entries into the per-template note-options catalog as
 * `PendingReview` rows; entries that match an Approved option are deduped silently.
 *
 * Empty for inspection items where the tech only picked from the catalog or AI list.
 */
export interface FreehandNoteEntry {
    note: string;
    recommendation: string | null;
}

/**
 * Base interface for all runtime inspection items
 * Contains common properties shared across all item types
 */
/**
 * When adding new type values, also update the backend enum in
 * Leo.Cars.Raptor.Model/Inspections/InspectionItemMetadata.cs (InspectionItemMetadataType)
 */
export interface InspectionItemMetadataItem {
    key: string;
    label?: string | null;
    value?: string | null;
    type: 'ExternalLink' | 'PlainText';
}

export interface InspectionItemBase {
    attachments: InspectionItemAttachment[];
    id: string;
    branchId: string;
    tenantId: string;
    inspectionId: string;
    name: string;
    /**
     * Whether this item is the Tech Check walk-around. Server-computed from the item name by the shared
     * classifier and read-only here, so the client never re-derives the rule.
     */
    isTechCheck?: boolean;
    isPhotoRequired: boolean;
    isVideoRequired: boolean;
    isRequired: boolean;
    isHidden: boolean;
    knowledgeHintDescription: string | null;
    knowledgeHintImpact: string | null;
    notes: string[];
    recommendations: string[];
    /**
     * Write-back target for this item's value, snapshotted from the template when the inspection
     * was created. Server-owned: the API re-hydrates it from persistence on save, so anything sent
     * here is ignored. Read-only as far as the client is concerned.
     */
    binding?: InspectionItemBinding;
    /**
     * Per-item generic key-value metadata. Mirrors the backend
     * InspectionItem.Metadata navigation; populated for inspection detail/get-by-id reads.
     */
    metadata?: InspectionItemMetadataItem[];
    /**
     * Sidecar of tech-authored freehand entries that the server materializes into the
     * per-item note-options catalog as PendingReview rows on inspection save. Optional so
     * existing fixtures and snapshots that never carry it continue to type-check; treat
     * undefined as empty.
     */
    freehandEntries?: FreehandNoteEntry[];
    sectionId: string;
    /** Composite-FK component back to the parent InspectionTemplateItem the inspection item
     * was created from. Server-managed (set by InspectionMapper at inspection-create time);
     * the FE round-trips it on PUT so server-side flows that need the parent template item
     * (e.g., MaterializeFreehandNoteOptions) can resolve it without a DB lookup. */
    templateItemId?: string | null;
    order: number;
    itemType: InspectionItemType;
    mediaCount?: { photo: number; video: number };
}

/**
 * Traffic light inspection item (runtime)
 * Contains the result of pass/warning/fail assessment
 */
export interface TrafficLightInspectionItem extends InspectionItemBase {
    itemType: InspectionItemType.TrafficLight;
    result?: InspectionItemResult;
    resultId: InspectionItemResultType;
    allowPass: boolean;
    allowWarning: boolean;
    allowFail: boolean;
    passLabel?: string;
    warningLabel?: string;
    failLabel?: string;
    quoteItemId?: string | null;
}

/**
 * Numeric range inspection item (runtime)
 * Contains the measured numeric value
 */
export interface NumericRangeInspectionItem extends InspectionItemBase {
    itemType: InspectionItemType.NumericRange;
    actualValue: number | null;
    minValue: number;
    maxValue: number;
    unit: string;
    decimalPlaces: number;
    constraints: NumericRangeConstraints;
    passLabel?: string | null;
    warningLabel?: string | null;
    failLabel?: string | null;
    resultId?: InspectionItemResultType | null;
    result?: InspectionItemResult;
    quoteItemId?: string | null;
}

/**
 * Checkbox inspection item (runtime)
 * Cont ains the true/false value
 */
export interface CheckboxInspectionItem extends InspectionItemBase {
    itemType: InspectionItemType.Checkbox;
    value: boolean | null;
    trueLabel: string;
    falseLabel: string;
}

/**
 * Notes inspection item (runtime)
 * Contains rich text HTML content
 */
export interface NotesInspectionItem extends InspectionItemBase {
    itemType: InspectionItemType.Notes;
    content: string | null;
    placeholder?: string;
    maxLength?: number;
    inputType: NotesInputType;
}

/**
 * DateInput inspection item (runtime)
 * Contains date value for capturing dates like next service date, warranty expiration, etc.
 */
export interface DateInputInspectionItem extends InspectionItemBase {
    itemType: InspectionItemType.DateInput;
    dateValue: string | null;
    placeholder?: string;
    minDate?: string;
    maxDate?: string;
    /** Server-derived: 'fail' when a NextServiceDate-bound date is overdue, otherwise null. Never chosen by the technician. */
    resultId?: string | null;
}

/**
 * TriState inspection item (runtime)
 * Contains the positive / not-applicable / negative answer, null when never answered
 */
export interface TriStateInspectionItem extends InspectionItemBase {
    itemType: InspectionItemType.TriState;
    value: TriStateValue | null;
    positiveLabel: string;
    middleLabel: string;
    negativeLabel: string;
}

/**
 * Union type for all runtime inspection item types
 * Enables type-safe discrimination based on itemType property
 */
export type InspectionItem =
    | InspectionItemBase
    | TrafficLightInspectionItem
    | NumericRangeInspectionItem
    | CheckboxInspectionItem
    | NotesInspectionItem
    | DateInputInspectionItem
    | TriStateInspectionItem;

/**
 * Result of a traffic light inspection item
 */
export interface InspectionItemResult {
    id: InspectionItemResultType;
    branchId: string;
    tenantId: string;
    name: string;
}

/**
 * File attachment for an inspection item
 */
export interface InspectionItemAttachment extends File {
    id: string;
    branchId: string;
    tenantId: string;
    itemId: string;
    /**
     * ISO-8601 UTC timestamp stamped by the client when the attachment is first added to
     * local inspection state. Required by the backend for multi-attachment ordering
     * (Tech Check concat order). Backend rejects the save if missing.
     */
    createdDate: string;
    isLoading?: boolean;
}

/**
 * Enum for inspection item result types (Pass/Warning/Fail)
 * Used for traffic light items
 */
export enum InspectionItemResultType {
    Pass = 'pass',
    Warning = 'warning',
    Fail = 'fail',
}
